<template>
  <!-- Variant v1: unchanged -->
  <template v-if="variant === variants.v1">
    <section class="layout-pt-lg layout-pb-lg" :style="`background-color: var(--c-${vars.backgroundColor})`">
      <div class="container">
        <div class="row y-gap-15 justify-between items-center">
          <div class="col-auto">
            <div v-html="vars.titleHtml"></div>
          </div>
          <div class="col-lg-6 col-md-9 tw-hidden lg:tw-block">
            <p v-for="(item, index) in items" :key="index" class="tw-mb-2">
              {{ item.description }}
            </p>
          </div>
        </div>

        <div data-anim-wrap class="row y-gap-30 pt-60 md:pt-30 tw-hidden lg:tw-flex">
          <div class="col-lg-3 col-sm-6" v-for="(team, index) in teams" :key="index">
            <div class="teamCard -type-1 tw-group" :data-anim-child="`slide-left delay-${index + 1}`">
              <div class="tw-overflow-hidden">
                <library-image
                  v-if="team.photo"
                  :src="team.photo"
                  class="tw-transition-all tw-duration-500 group-hover:tw-scale-110"
                  :alt="team.title || team.name"
                  :w-ratio="5"
                  :h-ratio="7"
                />
              </div>
              <div class="mt-3">
                <h4>{{ team.name }}</h4>
              </div>
              <div class="mt-1">
                <h5 :style="{ color: 'var(--c-secondary)' }">
                  {{ team.role }}
                </h5>
              </div>
            </div>
          </div>
        </div>

        <div
          class="relative mt-60 md:mt-40 sm:mt-20 js-section-slider lg:tw-hidden"
          data-anim="slide-left"
          data-gap="30"
          data-slider-cols="xl-3 lg-2 md-1 sm-1 base-1"
        >
          <div class="swiper-wrapper">
            <div class="swiper-slide" v-for="(team, index) in teams" :key="`latestblogs-blog-${index}`">
              <div class="teamCard">
                <div>
                  <library-image
                    v-if="team.photo"
                    :src="team.photo"
                    :alt="team.title || team.name"
                    :w-ratio="5"
                    :h-ratio="7"
                  />
                </div>
                <div class="mt-3">
                  <h4>{{ team.name }}</h4>
                </div>
                <div class="mt-1">
                  <h5 :style="{ color: 'var(--c-secondary)' }">
                    {{ team.role }}
                  </h5>
                </div>
              </div>
            </div>
          </div>

          <div class="d-flex mt-60 md:mt-30 max-lg:tw-items-center max-lg:tw-justify-center min-[1024px]:!tw-hidden">
            <button
              class="sliderNav -prev js-prev n-link !tw-bg-transparent !tw-text-black"
              style="border-color: black !important"
            >
              <i class="icon-left-arrow"></i>
            </button>
            <button
              class="sliderNav -next ml-20 js-next n-link !tw-bg-transparent !tw-text-black"
              style="border-color: black !important"
            >
              <i class="icon-right-arrow"></i>
            </button>
          </div>
        </div>
      </div>
    </section>
  </template>

  <!-- Variant v2: unchanged -->
<template v-else-if="variant === variants.v2">
  <section class="layout-pt-lg layout-pb-lg" :style="`background-color: var(--c-${vars.backgroundColor})`">
    <div class="container">
      <div class="row justify-between items-center">
        <div class="col-auto">
          <h2 class="text-62 md:text-38 uppercase fw-500" :style="{ color: `var(--c-${vars.textColor})` }">
            {{ vars.titleText }}
          </h2>
        </div>
      </div>

      <div
        class="relative mt-60 md:mt-40 sm:mt-20 js-section-slider"
        data-gap="30"
        data-slider-cols="xl-3 lg-3 md-2 sm-2 base-1"
      >
        <div class="swiper-wrapper">
          <div class="swiper-slide" v-for="(team, index) in teams" :key="`teams-member-${index}`">
            <div class="imageCard -type-2 hover-btn-explore" @click.prevent="toggleTeamDetailPopup(team)">
              <div class="imageCard__image relative">
                <library-image
                  v-if="team.photo"
                  :src="team.photo"
                  :alt="team.title || team.name"
                  :w-ratio="45"
                  :h-ratio="55"
                  image-class="img-ratio"
                />
                <div class="hover-btn-explore__item">
                  <button class="button text-white bg-dark-1 size-120 fw-500 rounded-full">View More</button>
                </div>
              </div>

              <div class="tw-mt-3 tw-flex tw-items-start tw-justify-between">
                <div class="tw-flex tw-flex-col tw-gap-3">
                  <h3 class="imageCard__title" :style="{ color: `var(--c-${vars.textColor})` }">
                    {{ team.name }}
                  </h3>
                  <h6 class="imageCard__text text-accent-1">
                    {{ team.role }}
                  </h6>
                </div>

                <!-- LinkedIn ikonu sadece link varsa gözüksün -->
                <nuxt-link
                  v-if="team.linkedin"
                  :to="team.linkedin"
                  class="n-link"
                  target="_blank"
                  rel="noopener noreferrer"
                  :external="true"
                  aria-label="LinkedIn profile"
                  @click.stop
                >
                  <svg width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="18" cy="18" r="17.5" fill="white" stroke="var(--c-secondary)" />
                    <path
                      d="M14.6276 13.3334C14.6274 13.687 14.4868 14.0261 14.2366 14.276C13.9864 14.5259 13.6472 14.6662 13.2936 14.6661C12.94 14.6659 12.6009 14.5252 12.351 14.2751C12.1011 14.0249 11.9608 13.6857 11.9609 13.3321C11.9611 12.9784 12.1018 12.6394 12.3519 12.3894C12.6021 12.1395 12.9413 11.9992 13.2949 11.9994C13.6486 11.9996 13.9876 12.1402 14.2376 12.3904C14.4875 12.6406 14.6278 12.9798 14.6276 13.3334Z"
                      fill="var(--c-primary)"
                    />
                    <path
                      d="M18.8809 15.6534H16.2276V24.0001H18.8543V19.6201C18.8543 17.1801 22.0343 16.9534 22.0343 19.6201V24.0001H24.6676V18.7134C24.6676 14.6001 19.9609 14.7534 18.8543 16.7734L18.8809 15.6534Z"
                      fill="var(--c-primary)"
                    />
                  </svg>
                </nuxt-link>
              </div>
            </div>
          </div>
        </div>

        <div class="d-flex mt-60 md:mt-30">
          <button class="sliderNav -prev -transparent js-prev">
            <i class="icon-left-arrow"></i>
          </button>
          <button class="sliderNav -next -transparent ml-20 js-next">
            <i class="icon-right-arrow"></i>
          </button>
        </div>
      </div>
    </div>
  </section>

  <popup v-if="selectedMember && popupConfig.isActive" :config="popupConfig" custom-width="90%">
    <template #content>
      <div class="tw-flex tw-flex-col tw-gap-8 lg:tw-grid lg:tw-grid-cols-12">
        <div class="tw-col-span-12 tw-w-full lg:tw-col-span-5">
          <library-image
            v-if="selectedMember.popup_photo"
            :src="selectedMember.popup_photo"
            :alt="selectedMember.title"
            image-class="tw-w-full tw-object-cover"
          />
          <div class="tw-aspect-video tw-w-full" v-else-if="selectedMember.popup_video">
            <library-iframe :src="selectedMember.popup_video" class="tw-h-full tw-w-full" />
          </div>
        </div>

        <div class="tw-col-span-12 lg:tw-col-span-7">
          <h3 class="uppercase fw-500 tw-col-span-12 tw-pb-2 tw-text-3xl !tw-text-white md:tw-text-5xl">
            {{ selectedMember.name }}
          </h3>
          <h6 class="text-accent-1 tw-mb-6 tw-mt-2">
            {{ selectedMember.role }}
          </h6>

          <div class="!tw-text-white" v-html="selectedMember.popup_description" />

          <div class="tw-mt-8 tw-flex tw-items-center tw-gap-4">
            <!-- LinkedIn sadece varsa -->
            <nuxt-link
              v-if="selectedMember.linkedin"
              :to="selectedMember.linkedin"
              class="n-link"
              target="_blank"
              rel="noopener noreferrer"
              :external="true"
              aria-label="LinkedIn profile"
            >
              <svg width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="18" cy="18" r="17.5" fill="white" stroke="var(--c-secondary)" />
                <path
                  d="M14.6276 13.3334C14.6274 13.687 14.4868 14.0261 14.2366 14.276C13.9864 14.5259 13.6472 14.6662 13.2936 14.6661C12.94 14.6659 12.6009 14.5252 12.351 14.2751C12.1011 14.0249 11.9608 13.6857 11.9609 13.3321C11.9611 12.9784 12.1018 12.6394 12.3519 12.3894C12.6021 12.1395 12.9413 11.9992 13.2949 11.9994C13.6486 11.9996 13.9876 12.1402 14.2376 12.3904C14.4875 12.6406 14.6278 12.9798 14.6276 13.3334Z"
                  fill="var(--c-primary)"
                />
                <path
                  d="M18.8809 15.6534H16.2276V24.0001H18.8543V19.6201C18.8543 17.1801 22.0343 16.9534 22.0343 19.6201V24.0001H24.6676V18.7134C24.6676 14.6001 19.9609 14.7534 18.8543 16.7734L18.8809 15.6534Z"
                  fill="var(--c-primary)"
                />
              </svg>
            </nuxt-link>

            <nuxt-link
              v-if="selectedMember.popup_button_link"
              :to="selectedMember.popup_button_link"
              :external="true"
              class="outlined n-link"
              :class="`n-${selectedMember.popup_button_type || 'quaternary'}`"
              target="_blank"
              rel="noopener noreferrer"
            >
              {{ selectedMember.popup_button_label }}
              <span v-if="selectedMember.popup_button_type?.toString().includes('secondary icon')" class="btn-arrow">
                <svg width="23" height="9" viewBox="0 0 23 9" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M22.3536 4.85355C22.5488 4.65829 22.5488 4.34171 22.3536 4.14645L19.1716 0.964466C18.9763 0.769204 18.6597 0.769204 18.4645 0.964466C18.2692 1.15973 18.2692 1.47631 18.4645 1.67157L21.2929 4.5L18.4645 7.32843C18.2692 7.52369 18.2692 7.84027 18.4645 8.03553C18.6597 8.2308 18.9763 8.2308 19.1716 8.03553L22.3536 4.85355ZM0 4.5V5H22V4.5V4H0V4.5Z"
                    fill="var(--c-text_primary)"
                  />
                </svg>
              </span>
            </nuxt-link>
          </div>
        </div>
      </div>
    </template>
  </popup>
</template>


  <!-- Variant v3: updated with popup logic -->
  <template v-else-if="variant === variants.v3">
    <section class="layout-pt-lg layout-pb-lg" :style="`background-color: var(--c-${vars.backgroundColor})`">
      <div class="container">
        <!-- Header & Tabs (güncellendi) -->
        <div class="row justify-between items-center">
          <div class="col-auto">
            <h2 class="mb-4">{{ vars.titleText }}</h2>
          </div>
          <div class="col-auto">
            <div class="tw-flex tw-flex-wrap tw-gap-6 lg:tw-gap-12">
              <button v-for="(tab, idx) in tabs" :key="idx" @click="selectedTab = idx" class="n-link tw-px-4 tw-py-2">
                <h5
                  :style="{
                    color: selectedTab === idx ? (idx === 2 ? '#66C9CF' : '#8FBB54') : 'inherit',
                  }"
                >
                  {{ tab.title }}
                </h5>
              </button>
            </div>
          </div>
        </div>

        <!-- Residential Team Slider -->
        <div
          v-show="selectedTab === 0"
          class="relative mt-60 md:mt-40 sm:mt-20 js-section-slider"
          data-gap="30"
          data-slider-cols="xl-3 lg-3 md-2 sm-2 base-1"
        >
          <div class="swiper-wrapper">
            <div v-for="(m, i) in team" :key="`res-${i}`" class="swiper-slide">
              <div class="imageCard -type-2" @click.prevent="toggleTeamDetailPopup(m)">
                <div class="imageCard__image relative">
                  <library-image v-if="m.photo" :src="String(m.photo)" :alt="String(m.name)" />
                  <library-iframe v-else-if="m.video" :src="String(m.video)" />
                </div>
                <h4
                  class="imageCard__text tw-mt-4 tw-line-clamp-4 tw-cursor-pointer"
                  :style="{ color: `var(--c-${vars.textColor})` }"
                >
                  {{ m.name }}
                </h4>
                <h5
                  class="imageCard__text tw-mt-2 tw-line-clamp-4 tw-cursor-pointer"
                  :style="{ color: `var(--c-${vars.textColor})` }"
                >
                  {{ m.role }}
                </h5>
              </div>
            </div>
          </div>
          <div class="d-flex mt-60 md:mt-30">
            <button class="sliderNav -prev js-prev">
              <i class="icon-left-arrow"></i>
            </button>
            <button class="sliderNav -next js-next ml-20">
              <i class="icon-right-arrow"></i>
            </button>
          </div>
        </div>

        <!-- Management Slider -->
        <div
          v-show="selectedTab === 1"
          class="relative mt-60 md:mt-40 sm:mt-20 js-section-slider"
          data-gap="30"
          data-slider-cols="xl-3 lg-3 md-2 sm-2 base-1"
        >
          <div class="swiper-wrapper">
            <div v-for="(m, i) in management" :key="`mgmt-${i}`" class="swiper-slide">
              <div class="imageCard -type-2" @click.prevent="toggleTeamDetailPopup(m)">
                <div class="imageCard__image relative">
                  <library-image v-if="m.photo" :src="String(m.photo)" :alt="String(m.name)" />
                  <library-iframe v-else-if="m.video" :src="String(m.video)" />
                </div>
                <h4
                  class="imageCard__text tw-mt-4 tw-line-clamp-4 tw-cursor-pointer"
                  :style="{ color: `var(--c-${vars.textColor})` }"
                >
                  {{ m.name }}
                </h4>
                <h5
                  class="imageCard__text tw-mt-2 tw-line-clamp-4 tw-cursor-pointer"
                  :style="{ color: `var(--c-${vars.textColor})` }"
                >
                  {{ m.role }}
                </h5>
              </div>
            </div>
          </div>
          <div class="d-flex mt-60 md:mt-30">
            <button class="sliderNav -prev js-prev">
              <i class="icon-left-arrow"></i>
            </button>
            <button class="sliderNav -next js-next ml-20">
              <i class="icon-right-arrow"></i>
            </button>
          </div>
        </div>

        <!-- Homes Slider -->
        <div
          v-show="selectedTab === 2"
          class="relative mt-60 md:mt-40 sm:mt-20 js-section-slider"
          data-gap="30"
          data-slider-cols="xl-3 lg-3 md-2 sm-2 base-1"
        >
          <div class="swiper-wrapper">
            <div v-for="(m, i) in homes" :key="`home-${i}`" class="swiper-slide">
              <div class="imageCard -type-2" @click.prevent="toggleTeamDetailPopup(m)">
                <div class="imageCard__image relative">
                  <library-image v-if="m.photo" :src="String(m.photo)" :alt="String(m.name)" />
                  <library-iframe v-else-if="m.video" :src="String(m.video)" />
                </div>
                <h4
                  class="imageCard__text tw-mt-4 tw-line-clamp-4 tw-cursor-pointer"
                  :style="{ color: `var(--c-${vars.textColor})` }"
                >
                  {{ m.name }}
                </h4>
                <h5
                  class="imageCard__text tw-mt-2 tw-line-clamp-4 tw-cursor-pointer"
                  :style="{ color: `var(--c-${vars.textColor})` }"
                >
                  {{ m.role }}
                </h5>
                <button
                  class="n-arrow mt-3"
                  style="display: inline-flex; align-items: center"
                  onmouseover="this.querySelector('svg').style.transform='translateX(5px)'"
                  onmouseout="this.querySelector('svg').style.transform='translateX(0)'"
                >
                  View Details
                  <svg
                    width="23"
                    height="9"
                    viewBox="0 0 23 9"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    style="margin-left: 8px; transition: transform 0.3s ease; transform: translateX(0)"
                  >
                    <path
                      d="M22.3536 5.0098C22.5488 4.81454 22.5488 4.49796 22.3536 4.3027L19.1716 1.12072C18.9763 0.925454 18.6597 0.925454 18.4645 1.12072C18.2692 1.31598 18.2692 1.63256 18.4645 1.82782L21.2929 4.65625L18.4645 7.48468C18.2692 7.52369 18.2692 7.84027 18.4645 8.03553C18.6597 8.2308 18.9763 8.2308 19.1716 8.03553L22.3536 5.0098ZM0 4.65625V5.15625H22V4.65625V4.15625H0V4.65625Z"
                      fill="#8FBB54"
                    />
                  </svg>
                </button>
              </div>
            </div>
          </div>
          <div class="d-flex mt-60 md:mt-30">
            <button class="sliderNav -prev js-prev">
              <i class="icon-left-arrow"></i>
            </button>
            <button class="sliderNav -next js-next ml-20">
              <i class="icon-right-arrow"></i>
            </button>
          </div>
        </div>
      </div>
    </section>
  </template>
</template>

<script lang="ts">
import { defineComponent, nextTick } from 'vue';
import { ComponentMixin } from '~/mixins/component.mixin';
import Popup from '~/components/common/popup/Popup.vue';
import type { Swiper } from 'swiper';

export default defineComponent({
  name: 'OurTeam',
  mixins: [ComponentMixin],
  components: { Popup },

  data() {
    return {
      selectedMember: null as any | null,
      popupConfig: { isActive: false },
      selectedTab: 0,
      variants: {
        v1: 'v1',
        v2: 'v2',
        v3: 'v3',
      },
      swiper: null as Swiper | null,
    };
  },

  computed: {
    // V1 için
    items(): any[] {
      return this.groupedVariables?.items || [];
    },
    teams(): any[] {
      return this.groupedVariables?.teams || [];
    },
    // V2 için
    tabs(): any[] {
      return this.groupedVariables?.tabs || [];
    },
    // V3 için
    team(): any[] {
      return this.groupedVariables?.teams || [];
    },
    management(): any[] {
      return this.groupedVariables?.management || [];
    },
    homes(): any[] {
      return this.groupedVariables?.homes || [];
    },
  },

  mounted() {
    this.initSectionSlider &&
      this.initSectionSlider({
        observer: true,
        observeParents: true,
        lazy: false,
      });
  },

  methods: {
    toggleTeamDetailPopup(team: any | null = null, value = true): void {
      if (team) {
        this.selectedMember = team;
        this.popupConfig.isActive = value;
      }
    },
    onSwiper(swiper: Swiper) {
      this.swiper = swiper;
    },
    onSlideChange() {
      if (!this.swiper) return;
    },
  },

  watch: {
    selectedTab() {
      nextTick(() => {
        if (this.swiper) {
          this.swiper.update();
          this.swiper.slideTo(0);
        }
      });
    },
  },
});
</script>

<style scoped>
.special-colors a.sliderNav:hover,
button.sliderNav:hover {
  background-color: var(--c-primary) !important;
  color: white !important;
  border-color: var(--c-primary) !important;
}
.team-member-popup {
  width: 100%;
  max-width: 1000px;
  margin: 0 auto;
  overflow: hidden;
  background-color: white;
}

.team-member-image-placeholder {
  background-color: #9a9a9a;
  width: 100%;
  height: 100%;
}

@media (max-width: 768px) {
  .team-member-popup {
    max-width: 90%;
  }
}
</style>
